import * as React from 'react'
import type {
  DefaultValues,
  FieldValues,
  Mode,
  SubmitErrorHandler,
  SubmitHandler,
  UseFormMethods
} from 'react-hook-form'
import { FormProvider, useForm } from 'react-hook-form'

import {
  type FormCustomContextType,
  FormCustomContext
} from './useFormCustomContext'

type FormProps<TFormData extends FieldValues> = Omit<
  React.HTMLAttributes<HTMLFormElement>,
  'onSubmit' | 'onError' | 'children'
> & {
  defaultValues?: DefaultValues<TFormData>
  validationMode?: Mode
  onSubmit: SubmitHandler<TFormData>
  onError?: SubmitErrorHandler<TFormData>
  noValidate?: boolean
  children:
    | React.ReactNode
    | ((methods: UseFormMethods<TFormData>) => React.ReactNode)
}

export const Form = <TFormData extends FieldValues>(
  props: FormProps<TFormData> & FormCustomContextType
) => {
  const {
    children,
    defaultValues,
    validationMode = 'onBlur',
    onSubmit,
    onError,
    appearance,
    theme,
    ...rest
  } = props

  const methods = useForm<TFormData>({
    defaultValues,
    mode: validationMode
  })

  return (
    <FormProvider {...methods}>
      <FormCustomContext.Provider value={{ appearance, theme }}>
        <form
          aria-label="form"
          onSubmit={methods.handleSubmit(onSubmit, onError)}
          {...rest}
        >
          {typeof children === 'function' ? children(methods) : children}
        </form>
      </FormCustomContext.Provider>
    </FormProvider>
  )
}

Form.displayName = 'Form'
